import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Message from "./Message";
import MessageReceived from "./MessageReceived";
import moment from "moment";
import { useAuthService } from "../../Context/AuthContext";
import DateSeparator from "./DateSeparator";
import EditMessageContainer from "./EditMessageContainer";
import ReplyMessageContainer from "./ReplyMessageContainer";
import { contextMenu } from "react-contexify";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  createDateSeparators,
  findTranslatedTextForUser,
} from "../../Helpers/chatHelper/ChatHelper";
import ChatInputComponent from "./ChatInputComponent";
import BlockedChatModal from "./BlockedChatModal";
import { isSimpleAndBlockedConversation } from "../../Helpers/chatHelper/ConversationHelper";
import { useExpanded } from "../../hooks/useExpanded";
import MessageMoodSelector from "./MessageMoodSelector";
import { displayAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import { displayName } from "../../Helpers/chatHelper/NameHelper";
import IconButton from "../GenericComponents/IconButton";
import { ReactComponent as Camera } from "../../Assets/iconsv2/generic/Video.svg";
import { ReactComponent as Phone } from "../../Assets/iconsv2/generic/Phone.svg";
import { useDropzone } from "react-dropzone";
import { useNotify } from "../../Helpers/Notifications";
import { useTranslate, useTolgee } from '@tolgee/react';
import { useConversationService } from "../../Context/ConversationsContext";

const ConversationView = ({
  messages,
  handleSendButton,
  inputValue,
  handleInputChange,
  shouldScrollBottom,
  handleReactToMessage,
  handleRemoveOwnMessageReaction,
  handleShowFullScreenImg,
  handleDeleteMessage,
  displayRightClickMenu,
  mediaInputFileRef,
  scrollHeightRef,
  selectedConversation,
  setMediaInput,
  mediaInput,
  fileInputRef,
  showEditMessageModal,
  selectedMessageForContextMenu,
  handleCloseEdit,
  messageInputRef,
  showReplyMessageModal,
  handleCloseReply,
  setFileInput,
  fileInput,
  shouldLoadMoreMessages,
  handleGetMoreMessages,
  showBlockedChatModal,
  setShowBlockedChatModal,
  handleUnblockContact,
  messageMood,
  setMessageMood,
  handleShowConversationDetailsMenu,
  handleCreateCallHeaderClick,
}) => {
  const { user } = useAuthService();
  const { t } = useTranslate();
  const tolgee = useTolgee();
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [showMoodSelector, togleModselector] = useExpanded();
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { markMessageAsRead, currentMessagesRef } = useConversationService();

  const onDrop = useCallback((acceptedFiles) => {
    const inputFile = acceptedFiles[0];

    const fileType = inputFile.type.split("/")[0];

    if (fileType === "image" || fileType === "video") {
      setMediaInput(inputFile);
      notifySuccess(t('chat.media_file_pasted'));
    } else {
      setFileInput(inputFile);
      notifySuccess(t('chat.file_pasted'));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const handleCreateCallHeaderClickStopPropagation = (e, selectedConversation, video) => {
    e.stopPropagation();
    handleCreateCallHeaderClick(selectedConversation, video)
  }

  const getGroupMembers = (groupMembers) => {
    let members = groupMembers.map((member) => {
      return member.name;
    });
    members.sort((a, b) => (a > b ? 1 : -1));
    return members.join(", ");
  };

  const getMessageToEdit = () => {
    if (
      !selectedMessageForContextMenu ||
      !selectedMessageForContextMenu.messageObject
    ) {
      return;
    }

    return selectedMessageForContextMenu.messageObject;
  };

  const getMessageToReply = () => {
    if (
      !selectedMessageForContextMenu ||
      !selectedMessageForContextMenu.messageObject
    ) {
      return;
    }

    return selectedMessageForContextMenu.messageObject;
  };

  const onConversationWrapperScroll = (e) => {
    contextMenu.hideAll();
    setIsAttachmentMenuOpen(false);
  };

  const markMessageAsReadIfNeeded = (id) => {
    let message = currentMessagesRef.current.find(
      (message) => message.id === id
    );

    if (message && message.user_id !== user.id) {
      let translationForCurrentUser = findTranslatedTextForUser(
        message.message_translations,
        user
      );

      if (translationForCurrentUser?.read_at === null) {
        markMessageAsRead(
          message.conversation_id,
          message.id,
          function (response) { },
          function (error) {
            console.log(error, "error mark as read");
          }
        );
      }
    }
  };

  const ongoingRequests = useRef(new Set());

  const observer = useRef(
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const itemId = parseInt(entry.target.id.split("-")[1], 10);
          // If the item is in view and it's not being processed already
          if (entry.isIntersecting && !ongoingRequests.current.has(itemId)) {
            ongoingRequests.current.add(itemId); // Add to ongoing request
            markMessageAsReadIfNeeded(itemId);
          }
        });
      },
      {
        rootMargin: "0px",
        threshold: 0.6, // Trigger when 10% of the item is in view
      }
    )
  );

  useEffect(() => {
    // Attach observer to each item after render
    messages.forEach((item) => {
      const element = document.getElementById(`message-${item.id}`);
      if (element) observer.current.observe(element);
    });

    return () => {
      // Cleanup observer when component unmounts or messages change
      messages.forEach((item) => {
        const element = document.getElementById(`message-${item.id}`);
        if (element) observer.current.unobserve(element);
      });
    };
  }, [messages]);

  useEffect(() => {
    if (!selectedConversation) {
      return;
    }
    let element = document.getElementById("messagesList");

    if (shouldScrollBottom.current === true) {
      element.scrollTo("top", element.scrollHeight);
    }
  }, [messages, shouldScrollBottom.current]);

  let allMessages = createDateSeparators(messages, tolgee.getLanguage());

  return (
    selectedConversation && (
      <Container>
        <Header
          onClick={() =>
            handleShowConversationDetailsMenu(selectedConversation)
          }
        >
          <UserSection>
            <UserAvatar
              style={selectedConversation?.quick_chat ? { border: "none" } : {}}
            >
              {displayAvatar(selectedConversation)}
            </UserAvatar>
            <Text>
              <ConversationName>
                {displayName(selectedConversation)}
              </ConversationName>
              {selectedConversation?.group && (
                <ConversationGroupInfo>
                  {getGroupMembers(selectedConversation?.other_participants)}
                </ConversationGroupInfo>
              )}
            </Text>
          </UserSection>

          {!selectedConversation?.with_emotii &&
            !selectedConversation?.quick_chat && (
              <ActionsSection>
                <IconButton
                  icon={<Camera />}
                  onClick={(e) =>
                    handleCreateCallHeaderClickStopPropagation(e, selectedConversation, true)
                  }
                />
                <IconButton
                  icon={<Phone />}
                  onClick={(e) =>
                    handleCreateCallHeaderClickStopPropagation(e, selectedConversation, false)
                  }
                />
              </ActionsSection>
            )}
        </Header>

        <ConversationWrapper id={"messagesList"} {...getRootProps()}>
          <input {...getInputProps()} />
          <InfiniteScroll
            dataLength={allMessages?.length}
            next={handleGetMoreMessages}
            hasMore={shouldLoadMoreMessages.current}
            onScroll={(e) => onConversationWrapperScroll(e)}
            scrollableTarget="messagesList"
            style={{ display: "flex", flexDirection: "column-reverse" }}
            inverse
          >
            {allMessages?.toReversed().map((message, index) => {
              if (message.id) {
                const isMyMessage = user.id === message.user_id;
                if (isMyMessage) {
                  return (
                    <Message
                      key={message.id}
                      text={message.original_message}
                      image={message.image}
                      status={message.status}
                      sentTime={moment(message.device_sent_date).format(
                        "HH:mm"
                      )}
                      reactions={message.reactions}
                      messageId={message.id}
                      conversationId={message.conversation_id}
                      handleShowFullScreenImg={handleShowFullScreenImg}
                      handleDeleteMessage={handleDeleteMessage}
                      messageDeleted={message.deleted}
                      displayRightClickMenu={displayRightClickMenu}
                      isMessageRead={message.read}
                      messageObject={message}
                      forwarded={message.forwarded}
                      messageParent={message.message_parent}
                      file={message.document}
                      video={message.video}
                      otherParticipants={selectedConversation?.other_participants}
                    />
                  );
                } else {
                  return (
                    <MessageReceived
                      key={message.id}
                      image={message.image}
                      messageId={message.id}
                      conversationId={message.conversation_id}
                      originalText={message.original_message}
                      language={message.language_name}
                      translatedText={findTranslatedTextForUser(
                        message.message_translations,
                        user
                      )}
                      sentTime={moment(message.device_sent_date).format(
                        "HH:mm"
                      )}
                      handleReactToMessage={handleReactToMessage}
                      handleRemoveOwnMessageReaction={handleRemoveOwnMessageReaction}
                      reactions={message.reactions}
                      handleShowFullScreenImg={handleShowFullScreenImg}
                      messageDeleted={message.deleted}
                      displayRightClickMenu={displayRightClickMenu}
                      messageUser={message.user}
                      selectedConversation={selectedConversation}
                      translatedFromLanguage={message.translated_from_language}
                      messageObject={message}
                      forwarded={message.forwarded}
                      messageParent={message.message_parent}
                      file={message.document}
                      video={message.video}
                    />
                  );
                }
              } else {
                return <DateSeparator key={index} date={message.date} />;
              }
            })}
          </InfiniteScroll>
          {showBlockedChatModal && (
            <BlockedChatModal
              handleUnblockContact={handleUnblockContact}
              setShowBlockedChatModal={setShowBlockedChatModal}
              contact={selectedConversation?.other_participants[0]}
            />
          )}
        </ConversationWrapper>

        {showEditMessageModal && (
          <EditMessageContainer
            message={getMessageToEdit()}
            handleCloseEdit={handleCloseEdit}
          />
        )}
        {showReplyMessageModal && (
          <ReplyMessageContainer
            message={getMessageToReply()}
            handleClose={handleCloseReply}
            user={user}
          />
        )}
        {showMoodSelector && (
          <MessageMoodSelector
            isMenuOpen={showMoodSelector}
            messageMood={messageMood}
            closeModal={togleModselector}
            setMood={setMessageMood}
          />
        )}

        {!isSimpleAndBlockedConversation(selectedConversation) && (
          <ChatInputComponent
            selectedConversation={selectedConversation}
            handleSendButton={handleSendButton}
            mediaInputFileRef={mediaInputFileRef}
            fileInputRef={fileInputRef}
            messageInputRef={messageInputRef}
            setMediaInput={setMediaInput}
            setFileInput={setFileInput}
            isAttachmentMenuOpen={isAttachmentMenuOpen}
            setIsAttachmentMenuOpen={setIsAttachmentMenuOpen}
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            showEditMessageModal={showEditMessageModal}
            showReplyMessageModal={showReplyMessageModal}
            handleCloseReply={handleCloseReply}
            handleCloseEdit={handleCloseEdit}
            togleModselector={togleModselector}
            messageMood={messageMood}
          />
        )}
      </Container>
    )
  );
};

export default ConversationView;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.chat.conversationBackground};
  height: 100%;
  width: 100%;
  position: relative;
  min-width: 0;
`;

const ConversationWrapper = styled.div`
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column-reverse;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
  align-items: center;
  background: ${({ theme }) => theme.chat.conversationHeader};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserAvatar = styled.div`
  position: relative;
  min-width: 42px;
  height: 42px;
  width: 42px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  gap: 2px;

`;
const ConversationName = styled.h1`
text-overflow:ellipsis;
white-space:nowrap;
overflow:hidden;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  letter-spacing: 0.04px;
  vertical-align: middle;
`;
const ConversationGroupInfo = styled.p`
  display: block;
  width: 100%;
  max-width: 100%; /* Ensures it doesn't push other content */
  overflow: hidden;
  max-height: 20px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.menu.infoText};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
`;
